import { FormEvent, MouseEvent, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { Button, Input, Textarea, Spinner } from '../../../shared';
import { ErrorText, FullRow, HalfRow } from '../Forms.styled';
import { ValidationCreateAddressType, defaultCreateAddressState } from '../types/userValidations';
import { createAddress, editAddress, setPrimaryAddress, retreiveUserAddresses, searchAddressMapbox } from '../../../api/addresses';
import { addAddresses, editedAddresses, setAddresses } from '../../../store/addresses/addressesSlice';
import { debouncedSearch } from '../../../utils/debounceSearch';
import { AddAddressTitle, AddressTitle, AddressWrapper, ButtonWrapper, AddressList } from './AddressCreateForm.styled';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';

import * as Sentry from '@sentry/react';

type AddressCreateFormProps = {
  addressToEdit?: any;
  shouldEdit?: boolean;
};

export const AddressCreateForm = (props: AddressCreateFormProps = { shouldEdit: false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<ValidationCreateAddressType>(
    defaultCreateAddressState
  );

  const [openAddressForm, setOpenAddressForm] = useState(props.shouldEdit);
  const [addressList, setAddressList] = useState([]);
  const [addressLocationValue, setAddressLocationValue] = useState('');
  const [zipcodeLocationValue, setZipcodeLocationValue] = useState('');
  const [stateLocationValue, setStateLocationValue] = useState('');
  const [cityLocationValue, setCityLocationValue] = useState('');
  const [coordLat, setCoordLat] = useState('');
  const [coordLong, setCoordLong] = useState('');
  const [isAddressSelected, setIsAddressSelected] = useState(props.shouldEdit);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (props.shouldEdit) {
      setAddressLocationValue(props.addressToEdit.address.address);
      setZipcodeLocationValue(props.addressToEdit.address.zipcode);
      setStateLocationValue(props.addressToEdit.address.state);
      setCityLocationValue(props.addressToEdit.address.city);
      setCoordLat(props.addressToEdit.address.coordLat || props.addressToEdit.address.coord_lat);
      setCoordLong(props.addressToEdit.address.coordLong || props.addressToEdit.address.coord_long);
    }
    return () => {
      setAddressLocationValue('');
      setZipcodeLocationValue('');
      setStateLocationValue('');
      setCityLocationValue('');
      setCoordLat('');
      setCoordLong('');
    };
  }, []);

  const handleAddAddress = (event: any) => {
    event.preventDefault();
    setOpenAddressForm(!openAddressForm);
  };

  const searchLocation = async (event: any) => {
    event.preventDefault();
    setAddressLocationValue(event.target.value);
    setIsAddressSelected(false);
    debouncedSearch(event.target.value, handleSearch);
  };

  const handleSearch = async (term: string) => {
    if (term.length >= 5) {
      setIsSearching(true);
      try {
        const features = await searchAddressMapbox(term);
        const mappedAddresses = features.map((f: any) => ({
          placeId: f.id,
          formattedAddress: f.place_name,
          _raw: f,
        }));
        setAddressList(mappedAddresses);
      } catch (error: any) {
        console.error('Error fetching addresses:', error);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const setSearchSelection = (event: MouseEvent, address: any) => {
    event.preventDefault();
    const raw = address._raw;
    const postcode = raw.context?.find((c: any) => c.id.includes('postcode'))?.text || '';
    const city = raw.context?.find((c: any) => c.id.includes('place'))?.text || '';
    const state = raw.context?.find((c: any) => c.id.includes('region'))?.text || '';
    const [longitude, latitude] = raw.geometry.coordinates;

    setAddressLocationValue(address.formattedAddress);
    setZipcodeLocationValue(postcode);
    setStateLocationValue(state);
    setCityLocationValue(city ? city : state);
    setCoordLat(latitude.toString());
    setCoordLong(longitude.toString());
    setAddressList([]);
    setIsAddressSelected(true);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors(defaultCreateAddressState);

    if (!isAddressSelected) {
      setErrors({
        ...defaultCreateAddressState,
        nonFieldErrors: [t('addresses.error.select.address')],
      });
      return;
    }

    const target = event.target as any;

    let paramsToSend: any = {
      name: target.name.value,
      address: target.address.value,
      zipcode: target.zipcode.value,
      state: target.state.value,
      city: target.city.value,
      instructions: target.description.value,
      description: target.description.value,
      coord_lat: coordLat,
      coord_long: coordLong,
    };

    if (props.shouldEdit) {
      paramsToSend = {
        addressId: props.addressToEdit.id,
        ...paramsToSend,
      };
    }

    await handleToastAsyncAction(
      async () => {
        if (props.shouldEdit) {
          const response = await editAddress({ params: paramsToSend });

          dispatch(editedAddresses({ ...paramsToSend, id: props.addressToEdit.id }));

          try {
            const updatedAddresses = await retreiveUserAddresses();
            dispatch(setAddresses(updatedAddresses));
          } catch (error) {
            console.error('Error reloading addresses after edit:', error);
          }

          return response;
        } else {
          const response = await createAddress({ params: paramsToSend });

          dispatch(addAddresses(response));

          try {
            await setPrimaryAddress({ id: response.id });
          } catch (error) {
            console.error('Error setting new address as primary:', error);
          }

          return response;
        }
      },
      {
        onSuccessMessage: t('addresses.create.toastify.success'),
        onErrorMessage: t('addresses.create.toastify.error'),
        dispatchAction: () => {
          setOpenAddressForm(!openAddressForm);

          if (!props.shouldEdit) {
            setAddressLocationValue('');
            setZipcodeLocationValue('');
            setStateLocationValue('');
            setCityLocationValue('');
            setCoordLat('');
            setCoordLong('');
          }
        },
        errorLogger: (error) => {
          if (error?.response?.status === 400) {
            setErrors({
              ...errors,
              ...error.response.data,
            });
            return;
          }

          console.error('Error creating/editing address:', error);
          Sentry.captureException(error);
        },
      }
    );
  };

  return (
    <div>
      {!props.shouldEdit ? (
        <AddAddressTitle onClick={handleAddAddress}>
          {openAddressForm ? '-' : '+'} {t('addresses.action.add.address')}
        </AddAddressTitle>
      ) : null}
      {openAddressForm ? (
        <>
          <form onSubmit={onSubmit}>
            <AddressWrapper>
              <AddressTitle>{t(`addresses.${props.shouldEdit ? 'edit' : 'add'}.address.title`)}</AddressTitle>
              
              <FullRow>
                <Input
                  label={t('addresses.street.address.title')}
                  name="address"
                  value={addressLocationValue}
                  onChange={searchLocation}
                  placeholder={t('addresses.street.address.placeholder')}
                  error={errors.address}
                  autoComplete="off"
                  required
                />
                <p>{t('addresses.street.address.helper')}</p>
                {isSearching && (
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Spinner />
                  </div>
                )}
                {addressList.length ? (
                  <AddressList onMouseLeave={() => setAddressList([])}>
                    {addressList.map((addr: any) => (
                      <li key={addr.placeId} onClick={(event) => setSearchSelection(event, addr)}>{addr.formattedAddress}</li>
                    ))}
                  </AddressList>
                ) : null}
              </FullRow>
              <FullRow>
                <Input
                  label={t('addresses.zipcode.title')}
                  name="zipcode"
                  value={zipcodeLocationValue}
                  placeholder={t('addresses.zipcode.placeholder')}
                  error={errors.zipcode}
                  disabled
                  required
                />
              </FullRow>
              <FullRow>
                <HalfRow>
                  <Input
                    label={t('addresses.state.title')}
                    name="state"
                    value={stateLocationValue}
                    placeholder={t('addresses.state.placeholder')}
                    error={errors.state}
                    disabled
                    required
                  />
                  <Input
                    label={t('addresses.city.title')}
                    name="city"
                    value={cityLocationValue}
                    placeholder={t('addresses.city.placeholder')}
                    error={errors.city}
                    disabled
                    required
                  />
                </HalfRow>
              </FullRow>
              <FullRow>
                <Textarea
                  label={t('addresses.delivInstructions.title')}
                  name="description"
                  placeholder={t('addresses.delivInstructions.placeholder')}
                  error={errors.instructions}
                  defaultValue={props.shouldEdit ? props.addressToEdit.address.description : ''}
                  required
                />
              </FullRow>
              <FullRow>
                <Input
                  label={t('addresses.tag.title')}
                  name="name"
                  placeholder={t('addresses.tag.placeholder')}
                  defaultValue={props.shouldEdit ? props.addressToEdit.name : ''}
                  error={errors.name}
                  required
                />
              </FullRow>
            </AddressWrapper>
            {errors.nonFieldErrors.length ? (
              <ErrorText>{errors.nonFieldErrors[0]}</ErrorText>
            ) : null}
            <ButtonWrapper>
              <Button
                title={t('shared.action.cancel')}
                id="cancelButton"
                onClick={handleAddAddress}
              />
              <Button title={t('shared.action.save')} type="submit" />
            </ButtonWrapper>
          </form>
        </>
      ) : null}
    </div>
  );
};
